// src/components/settings/sections/GeneralSettings.js
import React, {useEffect, useState} from 'react';
import { InformationCircleIcon, PhotoIcon } from '@heroicons/react/24/outline';
import {db} from "../../firebase";
import {toast} from "react-toastify";

const businessTypes = [
    { value: '', label: 'Select Business Type' },
    { value: 'sole_proprietor', label: 'Sole Proprietor' },
    { value: 'llc', label: 'Limited Liability Company (LLC)' },
    { value: 'pty_ltd', label: 'Proprietary Limited (Pty) Ltd' }, // Common in SA
    { value: 'partnership', label: 'Partnership' },
    { value: 'non_profit', label: 'Non-Profit Organization' },
    { value: 'other', label: 'Other' },
];

const industries = [
    { value: '', label: 'Select your industry (Optional)' },
    { value: 'retail', label: 'Retail' },
    { value: 'food_beverage', label: 'Food & Beverage' },
    { value: 'beauty_salon', label: 'Beauty & Salon' },
    { value: 'ecommerce', label: 'E-commerce' },
    { value: 'services', label: 'Services' },
    { value: 'health_wellness', label: 'Health & Wellness' },
    { value: 'automotive', label: 'Automotive' },
    { value: 'entertainment', label: 'Entertainment' },
    { value: 'hospitality', label: 'Hospitality' },
    { value: 'education', label: 'Education' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'technology', label: 'Technology' },
    { value: 'real_estate', label: 'Real Estate' },
    { value: 'other', label: 'Other' },
];

const GeneralSettings = ({ businessProfile, userId }) => {
    const [businessName, setBusinessName] = useState("");
    const [businessEmail, setBusinessEmail] = useState('');
    const [businessPhone, setBusinessPhone] = useState('');
    const [businessAddress, setBusinessAddress] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [businessWebsite, setBusinessWebsite] = useState('');
    const [industry, setIndustry] = useState('');
    const [primaryCurrency, setPrimaryCurrency] = useState('');
    const [businessType, setBusinessType] = useState('');
    const [numberOfLocations, setNumberOfLocations] = useState('');
    const [timezone, setTimezone] = useState('');

    useEffect(() => {
        setBusinessName(businessProfile?.businessName);
        setBusinessEmail(businessProfile?.businessEmail);
        setBusinessPhone(businessProfile?.businessPhone);
        setBusinessAddress(`${businessProfile?.streetAddress}, ${businessProfile?.city}, ${businessProfile?.stateProvince}, ${businessProfile?.country}, ${businessProfile?.postalCode}`);
        setBusinessWebsite(businessProfile?.businessWebsite);
        setIndustry(businessProfile?.industry);
        setPrimaryCurrency(businessProfile?.primaryCurrency);
        setBusinessType(businessProfile?.businessType);
        setNumberOfLocations(businessProfile?.numberOfLocations);
        setTimezone(businessProfile?.timezone);
    }, [businessProfile])

    const handleSave = (e) => {
        e.preventDefault();

        db.ref("localRewards").child(`/businessProfile/${userId}`).update({
            businessName: businessName,
            businessEmail: businessEmail,
            businessPhone: businessPhone,
            businessAddress: businessAddress,
            businessWebsite: businessWebsite,
            industry: industry,
            primaryCurrency: primaryCurrency,
            businessType: businessType,
            numberOfLocations: numberOfLocations,
            timezone: timezone,
        }).then(() => {
            toast.success('Business profile successfully saved!');
        }).catch((err) => {
            toast.error(err.message);
        })
    };

    const handleLogoUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoUrl(reader.result); // Set as Data URL for preview
                alert('Logo uploaded (preview only, not saved yet)!');
            };
            reader.readAsDataURL(file);
        }
    };

    return (
        <div className="space-y-6">
            <h2 className="text-2xl font-semibold text-gray-800">General Settings</h2>
            <p className="text-gray-600">Manage your basic business information and branding.</p>

            <form onSubmit={handleSave} className="space-y-6 mt-6">
                {/* Business Name */}
                <div>
                    <label htmlFor="businessName" className="block text-sm font-medium text-gray-700">Business Name</label>
                    <input
                        type="text"
                        id="businessName"
                        value={businessName}
                        onChange={(e) => setBusinessName(e.target.value)}
                        className="appearance-none rounded-none relative block w-full mt-1 p-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                </div>

                {/* Business Contact */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="businessEmail" className="block text-sm font-medium text-gray-700">Business
                            Email</label>
                        <input
                            type="email"
                            id="businessEmail"
                            value={businessEmail}
                            onChange={(e) => setBusinessEmail(e.target.value)}
                            className="appearance-none rounded-none relative block w-full mt-1 p-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>
                    <div>
                        <label htmlFor="businessPhone" className="block text-sm font-medium text-gray-700">Business
                            Phone</label>
                        <input
                            type="tel"
                            id="businessPhone"
                            value={businessPhone}
                            onChange={(e) => setBusinessPhone(e.target.value)}
                            className="appearance-none rounded-none relative block w-full mt-1 p-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>
                    <div>
                        <label htmlFor="businessWebsite" className="block text-sm font-medium text-gray-700">
                            Business Website
                        </label>
                        <input
                            type="text"
                            id="businessWebsite"
                            value={businessWebsite}
                            onChange={(e) => setBusinessWebsite(e.target.value)}
                            className="appearance-none rounded-none relative block w-full mt-1 p-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>
                    <div>
                        <label htmlFor="industry" className="block text-sm font-medium text-gray-700">
                            Industry
                        </label>
                        <select
                            name="industry"
                            id="industry"
                            value={industry}
                            onChange={(e) => setIndustry(e.target.value)}
                            className="appearance-none rounded-none relative block w-full mt-1 p-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        >
                            {industries.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="primaryCurrency" className="block text-sm font-medium text-gray-700">
                            Primary Currency
                        </label>
                        <input
                            type="text"
                            id="primaryCurrency"
                            value={primaryCurrency}
                            onChange={(e) => setPrimaryCurrency(e.target.value)}
                            className="appearance-none rounded-none relative block w-full mt-1 p-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>
                    <div>
                        <label htmlFor="businessType" className="block text-sm font-medium text-gray-700">
                            Business Type
                        </label>
                        <select
                            name="businessType"
                            id="businessType"
                            value={businessType}
                            onChange={(e) => setBusinessType(e.target.value)}
                            className="appearance-none rounded-none relative block w-full mt-1 p-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        >
                            {businessTypes.map((type) => (
                                <option key={type.value} value={type.value}>
                                    {type.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="numberOfLocations" className="block text-sm font-medium text-gray-700">
                            Number of Locations
                        </label>
                        <input
                            type="text"
                            id="numberOfLocations"
                            value={numberOfLocations}
                            onChange={(e) => setNumberOfLocations(e.target.value)}
                            className="appearance-none rounded-none relative block w-full mt-1 p-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>
                    <div>
                        <label htmlFor="timezone" className="block text-sm font-medium text-gray-700">
                            Timezone
                        </label>
                        <input
                            type="text"
                            id="timezone"
                            value={timezone}
                            onChange={(e) => setTimezone(e.target.value)}
                            className="appearance-none rounded-none relative block w-full mt-1 p-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>
                </div>

                {/* Business Address */}
                <div>
                    <label htmlFor="businessAddress" className="block text-sm font-medium text-gray-700">
                        Business Address
                    </label>
                    <textarea
                        id="businessAddress"
                        rows="2"
                        value={businessAddress}
                        onChange={(e) => setBusinessAddress(e.target.value)}
                        className="appearance-none rounded-none relative block w-full mt-1 p-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    ></textarea>
                </div>

                {/* Business Logo Upload */}
                <div>
                    <label className="block text-sm font-medium text-gray-700">Business Logo</label>
                    <div className="mt-1 flex items-center space-x-4">
                        {logoUrl ? (
                            <img src={logoUrl} alt="Business Logo"
                                 className="h-20 w-20 object-contain rounded-md border border-gray-200 p-1"/>
                        ) : (
                            <div
                                className="h-20 w-20 bg-gray-100 flex items-center justify-center rounded-md border border-gray-200">
                                <PhotoIcon className="h-10 w-10 text-gray-400"/>
                            </div>
                        )}
                        <input
                            type="file"
                            id="logo-upload"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            className="hidden"
                        />
                        <label
                            htmlFor="logo-upload"
                            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                            Change Logo
                        </label>
                    </div>
                    <p className="mt-2 text-xs text-gray-500">Upload a logo for your customer-facing loyalty pages.</p>
                </div>

                {/* Save Button */}
                <div className="pt-6 border-t border-gray-200 flex justify-end">
                    <button
                        type="submit"
                        className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        Save Changes
                    </button>
                </div>
            </form>
        </div>
    );
};

export default GeneralSettings;